import { useEffect, useState } from 'react';

import { useTheme } from '@mui/material';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Stack from '@mui/material/Stack';

import Avatar from '@webclient/components/avatar';

import useMarkdownEditor from '@webclient/hooks/useMarkdownEditor';
import {
  useDismissNotification,
  useProcessNotification,
} from '@webclient/hooks/useNotificationsQuery';

import { Notification } from '@webclient/shared-types/notifications';

import FileCheckIcon from '~icons/knowz-iconify/file-check';

import { MarkdownReadOnly } from '@knowz/knowz-ui';
import { useTranslate } from '@tolgee/react';
import {
  StyledAvatarContainer,
  StyledBadge,
  StyledNotificationContentStack,
  StyledText,
} from '@webclient/layouts/main/sections/header/sections/notifications/notifications-list/notification-item/styles';

type Props = {
  notification: Notification;
};

const NotificationItem = ({ notification }: Props) => {
  const { t } = useTranslate();
  const theme = useTheme();

  const processNotification = useProcessNotification();
  const dismissNotification = useDismissNotification();

  const {
    openMarkdownEditor,
    isLoading: isEditorLoading,
    sourceDetailsData,
    content,
  } = useMarkdownEditor();

  const handleAction = (notificationId: number, action: string) => {
    processNotification.mutate({ notificationId, action });
  };

  const handleDismiss = (notificationId: number) => {
    dismissNotification.mutate({ notificationId });
  };

  const handleOpenContent = () => {
    const entityId = notification?.contentReference?.entityId;
    if (!entityId) return;

    openMarkdownEditor(entityId, content ?? undefined);
  };

  const isSourceDetailsError = sourceDetailsData === null && !isEditorLoading;
  const [isPending, setIsPending] = useState(false);

  useEffect(() => {
    if (processNotification.isPending || dismissNotification.isPending) {
      setIsPending(true);
    }

    let timer = setTimeout(handleTimeout, 2000);

    function handleTimeout() {
      if (processNotification.isPending || dismissNotification.isPending) {
        setIsPending(false);
      } else {
        timer = setTimeout(handleTimeout, 2000);
      }
    }

    return () => {
      clearTimeout(timer);
    };
  }, [processNotification.isPending, dismissNotification.isPending]);

  return (
    <Stack
      direction="row"
      gap={4}
    >
      <StyledAvatarContainer>
        <Avatar
          size={34}
          url={notification.avatar}
        />
        {!notification.isDismissed && <StyledBadge />}
      </StyledAvatarContainer>

      <StyledNotificationContentStack spacing={3}>
        <Box sx={{ minHeight: 34 }}>
          <MarkdownReadOnly
            markdown={notification.body}
            openLinksInNewTab
          />
        </Box>

        {notification.contentReference?.name && (
          <Stack
            gap={1}
            direction="row"
          >
            <FileCheckIcon color={theme.palette.primary[500]} />

            <StyledText
              variant="textXs"
              weight="medium"
              isLoading={isEditorLoading}
              isSourceDetailsError={isSourceDetailsError}
              onClick={() => {
                if (!isEditorLoading) handleOpenContent();
              }}
            >
              {isSourceDetailsError
                ? `${t('common.notFound', 'Not found')}`
                : isEditorLoading
                  ? `${t('common.loading', 'Loading...')}`
                  : notification.contentReference.name}
            </StyledText>
          </Stack>
        )}

        <Stack
          direction="row"
          gap={1}
        >
          {/* TODO: See how to useTranslate in actions that came backend*/}
          {notification.actions.map((action, index) => (
            <Button
              variant="contained"
              key={action.actionId}
              onClick={() => handleAction(notification.id, action.actionId)}
              disabled={notification.isDismissed || isPending}
              size="xsmall"
              color={index === 0 ? 'primary' : 'secondary'}
            >
              {action.text}
            </Button>
          ))}

          {notification.actions.length === 0 && (
            <Button
              variant="contained"
              color="error"
              onClick={() => handleDismiss(notification.id)}
              disabled={notification.isDismissed || isPending}
              size="xsmall"
            >
              {t(
                'layout.main.sections.header.notifications.notificationsItem.dismiss',
                'Dismiss',
              )}
            </Button>
          )}
        </Stack>
      </StyledNotificationContentStack>
    </Stack>
  );
};

export default NotificationItem;
